import {
  CascaderField,
  DateRangePickerField,
  Form,
  NumericFormatField,
  type OperationType,
  SelectField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useForm } from '@/hooks';
import { isRS } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  useAccountingTypeOptions,
  useExpenditurePurposeOptions,
} from '../../hooks';
import { ExpenditurePurposeFormSchema } from '../../models';
import { expenditurePurposeCodeLength } from '../../utils';
import { getCodeMaxValue } from '../../utils/get-code-max-value';

type FormValues = z.infer<typeof ExpenditurePurposeFormSchema>;
type InitialFormValues = Partial<FormValues>;

type Props = {
  type: OperationType;
  initialValues?: InitialFormValues;
  onSubmit: (values: FormValues) => void;
};

export const ExpenditurePurposeForm: FC<Props> = ({
  type,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    setValue,
    resetField,
  } = useForm<InitialFormValues, FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(ExpenditurePurposeFormSchema),
  });

  const selectedLevel = watch('level');
  const upperOptions = useExpenditurePurposeOptions({
    upperLevel: selectedLevel ? selectedLevel - 1 : 0,
    showCode: true,
  });
  const accountingOptions = useAccountingTypeOptions({ showCode: true });

  const levelOptions = [
    {
      label: t('expenditure-purpose-level-1'),
      value: 1,
    },
    {
      label: t('expenditure-purpose-level-2'),
      value: 2,
    },
    {
      label: t('expenditure-purpose-level-3'),
      value: 3,
    },
  ].filter((option) => option.label);

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        name="level"
        label={t('level')}
        control={control}
        options={levelOptions}
        errors={errors}
        disabled={type === 'edit'}
        shouldCoexistOnChange
        onChange={(value) => {
          if (initialValues?.level !== value) {
            setValue('upperIds', undefined);
          } else {
            setValue('upperIds', initialValues?.upperIds);
          }
          if (value !== 1) {
            resetField('accountingId');
          }
        }}
        required
      />

      <SelectField
        name="accountingId"
        label={t('accounting-name')}
        control={control}
        options={accountingOptions.data}
        errors={errors}
        disabled={type === 'edit' || selectedLevel !== 1}
        required={selectedLevel === 1}
      />

      <CascaderField
        name="upperIds"
        label={t('upper-expenditure-purpose')}
        control={control}
        options={upperOptions.data}
        errors={errors}
        disabled={!selectedLevel || selectedLevel === 1}
        required={(selectedLevel ?? 0) > 1}
      />

      <NumericFormatField
        name="code"
        label={t('code')}
        control={control}
        errors={errors}
        disableSeparator
        max={getCodeMaxValue(expenditurePurposeCodeLength())}
        required
        disabled={!selectedLevel}
      />

      <TextInputField
        name="name"
        autocompleteKey="expenditurePurpose.name"
        label={t('name')}
        control={control}
        errors={errors}
        required
      />

      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
      />

      {isRS() && (
        <NumericFormatField
          name="sortOrder"
          label={t('display-sort-order')}
          control={control}
          errors={errors}
          required
          allowNegative={false}
        />
      )}

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
